import { useRef, useEffect } from "react";

export default function WaveCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");

    let width, height, dpr, extra;
    let animationId;
    const start = performance.now();

    const waves = [
      { amp: 48, freq: 0.0040, speed: 0.00012, y: 0.30, alpha: 0.12, thickness: 180 },
      { amp: 56, freq: 0.0042, speed: -0.00020, y: 0.38, alpha: 0.20, thickness: 190 },
      { amp: 64, freq: 0.0043, speed: 0.00022, y: 0.42, alpha: 0.26, thickness: 190 },
      { amp: 54, freq: 0.0041, speed: -0.00014, y: 0.58, alpha: 0.22, thickness: 180 },
    ];

    // initial phase assignment
    const n = waves.length;
    waves.forEach((wave, i) => {
      // evenly distribute around the sine cycle + small random jitter
      wave.phase = (i / n) * 2 * Math.PI + Math.random() * (Math.PI * 0.3);
    });

    function resize() {
      dpr = window.devicePixelRatio || 1;
      width = canvas.clientWidth;
      height = canvas.clientHeight;

      // calculate extra canvas space to avoid bottom clipping
      const bottomOffsets = waves.map(w => {
        const maxWaveOffset = w.amp + w.amp * 0.35; // maximum of waveOffset formula
        const maxThicknessMod = 6; // from drawWave
        return height * w.y + w.thickness + maxWaveOffset + maxThicknessMod;
      });
      extra = Math.max(0, Math.max(...bottomOffsets) - height);

      canvas.width = width * dpr;
      canvas.height = (height + extra) * dpr;

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    function waveOffset(wave, x, phase) {
      return (
        Math.sin(x * wave.freq + phase) * wave.amp +
        Math.sin(x * wave.freq * 0.5 + phase * 1.4) * (wave.amp * 0.35)
      );
    }

    function drawWave(wave, time) {
      const phase = time * wave.speed + wave.phase;
      const baseY = height * wave.y;

      // --- Fade configuration ---
      const stepX = 2;        // horizontal sampling interval for polygon points
      const maxFadeSteps = 6; // maximum fading layers
      const maxInset = 8;     // maximum vertical inset for fading
      let fadeSteps = 0;      // number of fading layers
      let fadeInset = 6;      // vertical inset range

      // --- Defensive handling ---
      fadeSteps = Math.min(maxFadeSteps, Math.max(0, Math.floor(fadeSteps)));
      fadeInset = Math.min(maxInset, Math.max(0, fadeInset));

      // --- Helper: draw a single polygon layer ---
      const drawPolygon = (inset = 0, alpha = wave.alpha) => {
        ctx.beginPath();

        // top edge (shift downward by inset)
        for (let x = 0; x <= width; x += stepX) {
          const y = baseY + waveOffset(wave, x, phase) + inset;
          ctx[x === 0 ? "moveTo" : "lineTo"](x, y);
        }

        // bottom edge (shift upward by inset)
        for (let x = width; x >= 0; x -= stepX) {
          const thicknessMod = Math.sin(x * wave.freq * 0.3 + phase * 0.8) * 6;
          const y = baseY + waveOffset(wave, x, phase) + wave.thickness + thicknessMod - inset;
          ctx.lineTo(x, y);
        }

        ctx.closePath();
        ctx.fillStyle = `rgba(255,120,120,${alpha})`;
        ctx.fill();
      };

      // --- FAST PATH: no fading ---
      if (fadeSteps === 0 || fadeInset === 0) {
        drawPolygon();
        return;
      }

      // --- FADE PATH: draw main ribbon + fading layers ---
      for (let layer = 0; layer <= fadeSteps; layer++) {
        const t = layer / (fadeSteps + 1);
        // overlayComp: mitigate dark multi-layer overlay
        const overlapComp = 0.4 + 0.6 / (fadeSteps + 1)
        // main layer: layer=0, inset=0, alpha=wave.alpha
        // fade layers: layer>0, inset increases, alpha decreases
        drawPolygon(layer * fadeInset * t, wave.alpha * (1 - t) * overlapComp);
      }
    }

    function animate(time) {
      ctx.clearRect(0, 0, width, height + extra);

      const t = time - start;
      for (const wave of waves) drawWave(wave, t);

      animationId = requestAnimationFrame(animate);
    }

    resize();
    window.addEventListener("resize", resize);
    animationId = requestAnimationFrame(animate);

    return () => {
      cancelAnimationFrame(animationId);
      window.removeEventListener("resize", resize);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{
        width: "100%",
        height: "60vh", // visible height
        display: "block",
        pointerEvents: "none",
      }}
    />
  );
}
